import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  Loader2,
  AlertCircle,
  RefreshCw,
  History,
  Settings,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { billingApi, getErrorMessage } from "@/services/api";

const CREDIT_TYPES = {
  sms: {
    name: "SMS Credits",
    icon: MessageSquare,
    color: "text-green-500 bg-green-100 dark:bg-green-950",
    unit: "SMS",
  },
};

export const Credits = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const initialType = searchParams.get("type") || "sms";

  const [activeTab, setActiveTab] = useState(initialType);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [autoRefillDialog, setAutoRefillDialog] = useState({
    open: false,
    type: null,
  });
  const [autoRefillSettings, setAutoRefillSettings] = useState({
    enabled: false,
    threshold: 10,
    amount: 100,
  });

  // ============================================
  // TanStack Query - Fetch Packages and Credits
  // ============================================
  const { data: creditsData, isLoading: loading } = useQuery({
    queryKey: ["billing-credits"],
    queryFn: async () => {
      const [packagesRes, creditsRes] = await Promise.all([
        billingApi.getCreditPackages(),
        billingApi.getCredits(),
      ]);
      return {
        packages: packagesRes.data.data || [],
        credits: creditsRes.data.data || {},
      };
    },
    meta: {
      onError: () => {
        toast.error("Failed to load credit information");
      },
    },
  });

  const packages = creditsData?.packages || [];
  const credits = creditsData?.credits || {};

  // ============================================
  // TanStack Query - Fetch History
  // ============================================
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ["credit-history", activeTab],
    queryFn: async () => {
      const response = await billingApi.getCreditHistory({
        type: activeTab,
        limit: 50,
        offset: 0,
      });
      return response.data.data || { transactions: [], total: 0 };
    },
  });

  const history = historyData?.transactions || [];

  // ============================================
  // TanStack Query - Auto Refill Mutation
  // ============================================
  const autoRefillMutation = useMutation({
    mutationFn: ({ type, settings }) =>
      billingApi.configureAutoRefill(type, settings),
    onSuccess: () => {
      toast.success("Auto-refill settings saved");
      setAutoRefillDialog({ open: false });
      queryClient.invalidateQueries({ queryKey: ["billing-credits"] });
    },
    onError: () => {
      toast.error(t("toast.failedSaveSettings"));
    },
  });

  useEffect(() => {
    if (searchParams.get("credits") === "success") {
      toast.success("Credits purchased successfully!");
      queryClient.invalidateQueries({ queryKey: ["billing-credits"] });
    } else if (searchParams.get("credits") === "cancelled") {
      toast.error("Credit purchase was canceled.");
    }
  }, [searchParams, queryClient]);

  const handlePurchase = async (packageId) => {
    try {
      setCheckoutLoading(packageId);
      const response = await billingApi.purchaseCredits(packageId);
      window.location.href = response.data.data.url;
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to start checkout"));
      setCheckoutLoading(null);
    }
  };

  const handleAutoRefillSave = () => {
    autoRefillMutation.mutate({
      type: autoRefillDialog.type,
      settings: autoRefillSettings,
    });
  };

  const openAutoRefillDialog = (type) => {
    const currentSettings = credits[type] || {};
    setAutoRefillSettings({
      enabled: currentSettings.autoRefillEnabled || false,
      threshold: currentSettings.autoRefillThreshold || 10,
      amount: currentSettings.autoRefillAmount || 100,
    });
    setAutoRefillDialog({ open: true, type });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderTransactionHistory = () =>
    history.length === 0 ? (
      <div className="text-center py-8 text-muted-foreground">
        {t("billing.credits.noTransactions")}
      </div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("common.date")}</TableHead>
            <TableHead>{t("common.type")}</TableHead>
            <TableHead>{t("common.description")}</TableHead>
            <TableHead className="text-right">{t("common.amount")}</TableHead>
            <TableHead className="text-right">
              {t("billing.overview.balance")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((tx) => (
            <TableRow key={tx.id}>
              <TableCell className="text-sm">
                {new Date(tx.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Badge variant={tx.amount > 0 ? "success" : "secondary"}>
                  {tx.amount > 0 ? "purchase" : "usage"}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-50 truncate">
                {tx.reference || "-"}
              </TableCell>
              <TableCell className="text-right">
                <span
                  className={tx.amount > 0 ? "text-green-600" : "text-red-600"}
                >
                  {tx.amount > 0 ? "+" : ""}
                  {tx.amount}
                </span>
              </TableCell>
              <TableCell className="text-right font-medium">
                {tx.balanceAfter}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("billing.credits.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("billing.purchaseCreditsDesc")}
          </p>
        </div>
      </div>

      {/* Credit Balances */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(CREDIT_TYPES).map(([type, config]) => {
          const Icon = config.icon;
          const balance =
            typeof credits[type] === "object"
              ? credits[type]?.balance || 0
              : credits[type] || 0;
          const autoRefill =
            typeof credits[type] === "object"
              ? credits[type]?.autoRefillEnabled
              : false;

          return (
            <Card
              key={type}
              className={activeTab === type ? "border-primary" : ""}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {config.name}
                </CardTitle>
                <div className={`p-2 rounded-lg ${config.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {balance.toLocaleString()}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  {autoRefill && (
                    <Badge variant="secondary" className="text-xs">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Auto-refill
                    </Badge>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => setActiveTab(type)}
                  >
                    Purchase →
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Purchase Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {Object.entries(CREDIT_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            return (
              <TabsTrigger key={type} value={type}>
                <Icon className="h-4 w-4 mr-2" />
                {config.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(CREDIT_TYPES).map(([type, config]) => {
          const balance =
            typeof credits[type] === "object"
              ? credits[type]?.balance || 0
              : credits[type] || 0;
          const typePackages =
            packages.find((p) => p.type === type)?.packages || [];

          return (
            <TabsContent key={type} value={type} className="mt-6 space-y-6">
              {/* Current Balance Card */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t("billing.credits.currentBalance")}</CardTitle>
                    <CardDescription>
                      {t("billing.credits.yourAvailable")}{" "}
                      {config.name.toLowerCase()}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAutoRefillDialog(type)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {t("billing.credits.autoRefill")}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold">
                      {balance.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">
                      {t("billing.credits.creditsAvailable")}
                    </span>
                  </div>
                  {typeof credits[type] === "object" &&
                    credits[type]?.autoRefillEnabled && (
                      <Alert className="mt-4">
                        <RefreshCw className="h-4 w-4" />
                        <AlertDescription>
                          {t("billing.credits.autoRefillEnabled", {
                            threshold: credits[type]?.autoRefillThreshold,
                            amount: credits[type]?.autoRefillAmount,
                          })}
                        </AlertDescription>
                      </Alert>
                    )}
                </CardContent>
              </Card>

              {/* Purchase Packages */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  {t("billing.credits.purchaseCredits")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  {typePackages.length > 0 ? (
                    typePackages.map((pkg) => (
                      <Card
                        key={pkg.id}
                        className={`relative ${
                          pkg.popular ? "border-primary shadow-lg mt-2" : ""
                        }`}
                      >
                        {pkg.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                            <Badge className="bg-primary">
                              <Sparkles className="h-3 w-3 mr-1" />
                              {t("billing.credits.bestValue")}
                            </Badge>
                          </div>
                        )}
                        <CardHeader className="text-center">
                          <CardTitle>{pkg.name}</CardTitle>
                          <div className="mt-2">
                            <div className="text-3xl font-bold">
                              {pkg.credits.toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {t("common.credits")}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="text-center">
                          <div className="text-2xl font-bold">
                            ${pkg.priceDisplay ?? (pkg.price / 100).toFixed(2)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            $
                            {pkg.pricePerCredit?.toFixed(3) ??
                              (pkg.price / 100 / pkg.credits).toFixed(3)}
                            /{config.unit}
                          </div>
                        </CardContent>
                        <CardFooter>
                          <Button
                            className="w-full"
                            variant={pkg.popular ? "default" : "secondary"}
                            onClick={() => handlePurchase(pkg.id)}
                            disabled={checkoutLoading === pkg.id}
                          >
                            {checkoutLoading === pkg.id && (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t("common.purchase")}
                          </Button>
                        </CardFooter>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-3 text-center py-8 text-muted-foreground">
                      No packages available for {config.name}
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    {t("billing.credits.purchaseHistory")}
                  </CardTitle>
                  <CardDescription>
                    {t("billing.credits.recentPurchases")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    renderTransactionHistory()
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Auto-Refill Dialog */}
      <Dialog
        open={autoRefillDialog.open}
        onOpenChange={(open) =>
          setAutoRefillDialog({ ...autoRefillDialog, open })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("billing.credits.autoRefill")}</DialogTitle>
            <DialogDescription>
              {t("billing.credits.autoRefillDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="auto-refill-toggle">
                {t("billing.credits.enableAutoRefill")}
              </Label>
              <Switch
                id="auto-refill-toggle"
                checked={autoRefillSettings.enabled}
                onCheckedChange={(checked) =>
                  setAutoRefillSettings({
                    ...autoRefillSettings,
                    enabled: checked,
                  })
                }
              />
            </div>

            {autoRefillSettings.enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="threshold">
                    {t("billing.credits.refillThreshold")}
                  </Label>
                  <Input
                    id="threshold"
                    type="number"
                    value={autoRefillSettings.threshold}
                    onChange={(e) =>
                      setAutoRefillSettings({
                        ...autoRefillSettings,
                        threshold: parseInt(e.target.value) || 0,
                      })
                    }
                    min={1}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">
                    {t("billing.credits.refillAmount")}
                  </Label>
                  <Select
                    value={autoRefillSettings.amount.toString()}
                    onValueChange={(value) =>
                      setAutoRefillSettings({
                        ...autoRefillSettings,
                        amount: parseInt(value),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">
                        100 {t("common.credits")}
                      </SelectItem>
                      <SelectItem value="250">
                        250 {t("common.credits")}
                      </SelectItem>
                      <SelectItem value="500">
                        500 {t("common.credits")}
                      </SelectItem>
                      <SelectItem value="1000">
                        1,000 {t("common.credits")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t("billing.credits.autoRefillWarning")}
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAutoRefillDialog({ open: false })}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAutoRefillSave}
              size={isMobile ? "mobile" : "default"}
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Credits;
